import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { SecondaryButton, TransparentBgButton } from 'components/Shared/Button'
import AssetSelectMenu from 'components/Settings/Meta/TestCampaign/AssetSelectMenu'
import YouTubeUrlInput from 'components/Recommendations/Campaign/Media/YouTube/YouTubeUrlInput'
import { useGoogleTestCampaignContext } from './testCampaignContext'
import { getActiveAdAccounts, getConversionsForAccount } from './testCampaignUtils'

export default function GoogleAssetSelectionStep({ assets, onCancel, error }) {
  const { t } = useTranslation()
  const { selectedAssets, selectAsset, createCampaign, loading } = useGoogleTestCampaignContext()

  const adAccounts = useMemo(() => getActiveAdAccounts(assets), [assets])

  const availableConversions = useMemo(
    () => getConversionsForAccount(selectedAssets.adAccount),
    [selectedAssets.adAccount],
  )

  const isValid = !!selectedAssets.adAccount

  const handleAdAccountChange = (asset) => {
    selectAsset('adAccount', asset)
    // Clear conversion if it was linked to the previous account
    selectAsset('conversionAction', null)
  }

  return (
    <div className="test-campaign-asset-selection">
      <div className="test-campaign-asset-selection__content">
        <h1>
          {t('Settings.GoogleAccess.TestCampaign.selectAssets', 'Run a Google Ads Test Campaign')}
        </h1>
        <p className="body-txt light larger mb-4">
          {t(
            'Settings.GoogleAccess.TestCampaign.selectAssetsDescription',
            'Select the Google Ads account to use. Optionally add a YouTube video to run a Video campaign instead of a Display campaign.',
          )}
        </p>

        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            {error.message ||
              t(
                'Settings.GoogleAccess.TestCampaign.error.createFailed',
                'Failed to create test campaign. Please try again.',
              )}
          </div>
        )}

        <div className="d-flex flex-column gap-3">
          <AssetSelectMenu
            assets={adAccounts}
            selectedAsset={selectedAssets.adAccount}
            onSelect={handleAdAccountChange}
            label={t('Settings.GoogleAccess.TestCampaign.adAccount', 'Google Ads Account')}
            placeholder={t(
              'Settings.GoogleAccess.TestCampaign.selectAdAccount',
              'Select Ad Account',
            )}
            required
          />
          <AssetSelectMenu
            assets={availableConversions}
            selectedAsset={selectedAssets.conversionAction}
            onSelect={(asset) => selectAsset('conversionAction', asset)}
            onRemove={() => selectAsset('conversionAction', null)}
            label={t(
              'Settings.GoogleAccess.TestCampaign.conversionAction',
              'Conversion Action (optional)',
            )}
            placeholder={t(
              'Settings.GoogleAccess.TestCampaign.selectConversionAction',
              'Select Conversion Action',
            )}
          />
          <div>
            <div className="body-txt bold mb-1">
              {t(
                'Settings.GoogleAccess.TestCampaign.youtubeVideo',
                'YouTube Video (optional — creates a Video campaign)',
              )}
            </div>
            <YouTubeUrlInput
              video={selectedAssets.youtubeVideo}
              onVideo={(video) => selectAsset('youtubeVideo', video)}
              onClear={() => selectAsset('youtubeVideo', null)}
            />
          </div>
        </div>
      </div>

      <div className="test-campaign-asset-selection__footer">
        <TransparentBgButton 
          text={t('common.cancel', 'Cancel')} 
          onClick={onCancel} 
          color="navy" 
          extraStyles={{ width: '200px' }}
        />

        <SecondaryButton
          className="bt"
          text={t('Settings.GoogleAccess.TestCampaign.runCampaign', 'Run Test Campaign')}
          fullWidth={false}
          onClick={createCampaign}
          disabled={!isValid || loading}
        />
      </div>
    </div>
  )
}

GoogleAssetSelectionStep.propTypes = {
  assets: PropTypes.array.isRequired,
  onCancel: PropTypes.func.isRequired,
  error: PropTypes.object,
}
